import { t } from '../../i18n';
import React, { memo, useEffect, useRef, useState } from 'react';
import { parseLoungeMarkup, serializeLoungeMarkup } from './loungeMarkup';

const TEXT_TYPES = [
  { value: 'paragraph', label: '본문' },
  { value: 'heading', label: '제목' },
  { value: 'small', label: '작은 글씨' },
  { value: 'quote', label: '인용문' },
];
const FONT_SIZES = [12, 14, 16, 18, 20, 24, 28, 32, 34, 36, 40, 48];
const ALIGNMENTS = [
  { value: 'left', label: '왼쪽', icon: '☰' },
  { value: 'center', label: '가운데', icon: '☰' },
  { value: 'right', label: '오른쪽', icon: '☰' },
];
const DEFAULT_TEXT_COLOR = '#ffffff';
const EDITOR_WHITE_PREVIEW_COLOR = '#111827';

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

function clamp(value, min, max, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

function normalizeColor(value, fallback = DEFAULT_TEXT_COLOR) {
  const raw = String(value || '').trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(raw)) return raw.toLowerCase();
  const rgb = raw.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) {
    return `#${[rgb[1], rgb[2], rgb[3]].map((x) => Math.max(0, Math.min(255, Number(x))).toString(16).padStart(2, '0')).join('')}`;
  }
  return fallback;
}

function normalizeFontWeight(value) {
  const raw = String(value || 'normal').toLowerCase();
  if (['bold', '600', '700'].includes(raw)) return 'bold';
  if (['medium', '500'].includes(raw)) return '500';
  return 'normal';
}

function normalizeInlineStyle(style = {}, fallback = {}) {
  return {
    fontSize: clamp(style.fontSize ?? fallback.fontSize, 12, 48, 16),
    fontWeight: normalizeFontWeight(style.fontWeight ?? fallback.fontWeight),
    color: normalizeColor(style.color ?? fallback.color),
    fontStyle: String(style.fontStyle ?? fallback.fontStyle ?? 'normal') === 'italic' ? 'italic' : 'normal',
    textDecoration: String(style.textDecoration ?? fallback.textDecoration ?? 'none').includes('underline') ? 'underline' : 'none',
  };
}

function normalizeStyle(style = {}) {
  const inline = normalizeInlineStyle(style);
  return {
    textType: ['paragraph', 'heading', 'small', 'quote'].includes(String(style.textType)) ? String(style.textType) : 'paragraph',
    fontSize: inline.fontSize,
    fontWeight: inline.fontWeight,
    color: inline.color,
    textAlign: ['left', 'center', 'right'].includes(String(style.textAlign)) ? String(style.textAlign) : 'left',
    fontStyle: inline.fontStyle,
    textDecoration: inline.textDecoration,
  };
}

function normalizeLinkInput(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}

function sameInlineStyle(a, b) {
  return a.fontSize === b.fontSize
    && a.fontWeight === b.fontWeight
    && a.color === b.color
    && a.fontStyle === b.fontStyle
    && a.textDecoration === b.textDecoration;
}

function mergeSegments(segments) {
  const merged = [];
  for (const raw of Array.isArray(segments) ? segments : []) {
    const text = String(raw?.text ?? '');
    if (!text) continue;
    const style = normalizeInlineStyle(raw?.style);
    const linkUrl = String(raw?.linkUrl ?? raw?.link_url ?? '');
    const prev = merged[merged.length - 1];
    if (prev && prev.linkUrl === linkUrl && sameInlineStyle(prev.style, style)) {
      prev.text += text;
    } else {
      merged.push({ text, style, linkUrl });
    }
  }
  return merged;
}

function normalizeSegments(block, baseStyle) {
  if (Array.isArray(block?.segments) && block.segments.length) {
    const normalized = mergeSegments(block.segments.map((segment) => ({
      text: String(segment?.text ?? ''),
      style: normalizeInlineStyle(segment?.style, baseStyle),
      linkUrl: String(segment?.linkUrl ?? segment?.link_url ?? ''),
    })));
    if (normalized.length) return normalized;
  }

  const text = String(block?.text ?? '');
  if (!text) return [];
  return [{
    text,
    style: normalizeInlineStyle(baseStyle),
    linkUrl: String(block?.linkUrl ?? block?.link_url ?? ''),
  }];
}

function normalizeImageDisplay(display = {}) {
  return {
    widthPercent: Math.round(clamp(display.widthPercent ?? display.width_percent, 20, 100, 100)),
    heightPx: Math.round(clamp(display.heightPx ?? display.height_px, 100, 720, 280)),
    objectFit: 'clip',
  };
}

export function createLoungeTextBlock() {
  return {
    id: uid(),
    type: 'text',
    text: '',
    style: {
      textType: 'paragraph',
      fontSize: 16,
      fontWeight: 'normal',
      color: DEFAULT_TEXT_COLOR,
      textAlign: 'left',
      fontStyle: 'normal',
      textDecoration: 'none',
    },
    segments: [],
    linkUrl: '',
  };
}

export function normalizeLoungeEditorBlocks(value) {
  const source = Array.isArray(value) ? value : [];
  const blocks = source
    .filter((block) => block && typeof block === 'object')
    .map((block) => {
      if (block.type === 'widget') {
        return {...block,id:block.id||uid(),type:'widget',offers:block.widgetType==='shop'&&Array.isArray(block.offers)?block.offers:[]};
      }
      if (block.type === 'image') {
        return {
          ...block,
          id: block.id || uid(),
          type: 'image',
          url: String(block.url || ''),
          alt: String(block.alt || ''),
          display: normalizeImageDisplay(block.display || block.imageDisplay || {}),
        };
      }
      const style = normalizeStyle(block.style);
      const segments = normalizeSegments(block, style);
      const text = segments.length ? segments.map((segment) => segment.text).join('') : String(block.text || '');
      return {
        ...block,
        id: block.id || uid(),
        type: 'text',
        text,
        style,
        segments,
        linkUrl: String(block.linkUrl ?? block.link_url ?? ''),
      };
    });
  return blocks.length ? blocks : [createLoungeTextBlock()];
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function editorColor(modelColor) {
  const normalized = normalizeColor(modelColor);
  return normalized === DEFAULT_TEXT_COLOR ? EDITOR_WHITE_PREVIEW_COLOR : normalized;
}

function segmentToHtml(segment) {
  const style = normalizeInlineStyle(segment?.style);
  const text = escapeHtml(segment?.text || '').replace(/\n/g, '<br>');
  const span = `<span data-lounge-color="${escapeHtml(style.color)}" style="font-size:${style.fontSize}px;font-weight:${escapeHtml(style.fontWeight)};color:${editorColor(style.color)};font-style:${escapeHtml(style.fontStyle)};text-decoration:${escapeHtml(style.textDecoration)}">${text}</span>`;
  const link = normalizeLinkInput(segment?.linkUrl || '');
  return link ? `<a href="${escapeHtml(link)}" data-lounge-link="${escapeHtml(link)}">${span}</a>` : span;
}

function blockToHtml(block) {
  const style = normalizeStyle(block?.style);
  const segments = normalizeSegments(block, style);
  if (!segments.length) return '';
  return segments.map(segmentToHtml).join('');
}

function isBlockElement(tagName) {
  return ['DIV', 'P', 'LI', 'BLOCKQUOTE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(tagName);
}

function pushSegment(target, text, style, linkUrl) {
  if (!text) return;
  const normalizedStyle = normalizeInlineStyle(style);
  const normalizedLink = String(linkUrl || '');
  const prev = target[target.length - 1];
  if (prev && prev.linkUrl === normalizedLink && sameInlineStyle(prev.style, normalizedStyle)) {
    prev.text += text;
  } else {
    target.push({ text, style: normalizedStyle, linkUrl: normalizedLink });
  }
}

function elementInlineStyle(element, inherited) {
  const next = { ...normalizeInlineStyle(inherited) };
  const tag = element.tagName;
  const dataColor = element.getAttribute?.('data-lounge-color');
  if (dataColor) {
    const modelColor = normalizeColor(dataColor, next.color);
    const visualColor = element.style?.color ? normalizeColor(element.style.color, modelColor) : modelColor;
    next.color = visualColor !== editorColor(modelColor) ? visualColor : modelColor;
  } else if (element.style?.color) {
    next.color = normalizeColor(element.style.color, next.color);
  }

  if (element.style?.fontSize) next.fontSize = clamp(parseFloat(element.style.fontSize), 12, 48, next.fontSize);
  if (element.style?.fontWeight) next.fontWeight = normalizeFontWeight(element.style.fontWeight);
  if (element.style?.fontStyle === 'italic') next.fontStyle = 'italic';
  if (String(element.style?.textDecoration || '').includes('underline')) next.textDecoration = 'underline';

  if (tag === 'STRONG' || tag === 'B') next.fontWeight = 'bold';
  if (tag === 'EM' || tag === 'I') next.fontStyle = 'italic';
  if (tag === 'U') next.textDecoration = 'underline';
  if (tag === 'FONT') {
    if (element.getAttribute('color')) next.color = normalizeColor(element.getAttribute('color'), next.color);
  }
  return next;
}

function serializeEditable(element, baseStyle) {
  const segments = [];

  const appendNewline = (style, linkUrl) => {
    const last = segments[segments.length - 1];
    if (!last || !last.text.endsWith('\n')) pushSegment(segments, '\n', style, linkUrl);
  };

  const walk = (node, inheritedStyle, inheritedLink, isRoot = false) => {
    if (node.nodeType === Node.TEXT_NODE) {
      pushSegment(segments, node.nodeValue || '', inheritedStyle, inheritedLink);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const elementNode = node;
    if (elementNode.tagName === 'BR') {
      pushSegment(segments, '\n', inheritedStyle, inheritedLink);
      return;
    }

    const nextStyle = elementInlineStyle(elementNode, inheritedStyle);
    const nextLink = elementNode.tagName === 'A'
      ? String(elementNode.getAttribute('data-lounge-link') || elementNode.getAttribute('href') || '')
      : inheritedLink;
    const blockLevel = !isRoot && isBlockElement(elementNode.tagName);

    if (blockLevel && segments.length && !segments[segments.length - 1].text.endsWith('\n')) {
      appendNewline(nextStyle, nextLink);
    }

    for (const child of Array.from(elementNode.childNodes || [])) {
      walk(child, nextStyle, nextLink, false);
    }

    if (blockLevel) appendNewline(nextStyle, nextLink);
  };

  const rootStyle = normalizeInlineStyle(baseStyle);
  for (const child of Array.from(element.childNodes || [])) {
    walk(child, rootStyle, '', false);
  }
  if (segments.length) {
    const last = segments[segments.length - 1];
    if (last.text === '\n') segments.pop();
    else if (last.text.endsWith('\n')) last.text = last.text.slice(0, -1);
  }
  return mergeSegments(segments);
}

function getCaretOffset(element) {
  const selection = window.getSelection?.();
  if (!selection || !selection.rangeCount || !element) return String(element?.innerText || '').length;
  const range = selection.getRangeAt(0);
  if (!element.contains(range.startContainer)) return String(element.innerText || '').length;
  const preRange = range.cloneRange();
  preRange.selectNodeContents(element);
  preRange.setEnd(range.startContainer, range.startOffset);
  return preRange.toString().length;
}

function splitSegmentsAt(segments, offset) {
  const before = [];
  const after = [];
  let remaining = Math.max(0, Number(offset || 0));
  for (const segment of segments) {
    const text = String(segment.text || '');
    if (remaining <= 0) {
      after.push({ ...segment });
      continue;
    }
    if (remaining >= text.length) {
      before.push({ ...segment });
      remaining -= text.length;
      continue;
    }
    const left = text.slice(0, remaining);
    const right = text.slice(remaining);
    if (left) before.push({ ...segment, text: left });
    if (right) after.push({ ...segment, text: right });
    remaining = 0;
  }
  return { before: mergeSegments(before), after: mergeSegments(after) };
}

function ToolbarButton({ active, disabled, title, children, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className={`h-9 min-w-9 px-2 rounded-md border text-sm font-semibold transition-colors disabled:opacity-40 ${
        active
          ? 'border-blue-400 bg-blue-50 text-blue-700'
          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  );
}

const EditableTextBlock = memo(function EditableTextBlock({
  block,
  disabled,
  revision,
  commandEditing,
  onFocusBlock,
  onSelectionActivity,
  onDomChange,
}) {
  const style = normalizeStyle(block.style);
  const isQuote = style.textType === 'quote';
  const isHeading = style.textType === 'heading';
  const isSmall = style.textType === 'small';

  return (
    <div
      data-editor-block-id={block.id}
      contentEditable={!disabled && !commandEditing}
      suppressContentEditableWarning
      spellCheck
      role="textbox"
      aria-multiline="true"
      data-placeholder={t('본문 내용을 입력하세요')}
      onFocus={(e) => {
        onFocusBlock(block.id);
        onSelectionActivity(e.currentTarget);
      }}
      onMouseUp={(e) => onSelectionActivity(e.currentTarget)}
      onKeyUp={(e) => onSelectionActivity(e.currentTarget)}
      onClick={(e) => {
        if (e.target.closest?.('a')) e.preventDefault();
        onFocusBlock(block.id);
        onSelectionActivity(e.currentTarget);
      }}
      onInput={(e) => onDomChange(block.id, e.currentTarget)}
      onPaste={(e) => {
        if (disabled) return;
        e.preventDefault();
        const text = e.clipboardData?.getData('text/plain') || '';
        document.execCommand('insertText', false, text);
      }}
      style={{
        fontSize: style.fontSize,
        fontWeight: style.fontWeight,
        color: editorColor(style.color),
        textAlign: style.textAlign,
        fontStyle: style.fontStyle,
        textDecoration: style.textDecoration,
        lineHeight: isHeading ? 1.35 : 1.75,
        minHeight: isHeading ? 42 : 34,
        overflowWrap: 'anywhere',
        whiteSpace: 'pre-wrap',
      }}
      className={`relative w-full rounded-sm px-1 py-1.5 outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 empty:before:pointer-events-none ${
        isHeading ? 'text-[1.25em]' : isSmall ? 'opacity-80' : ''
      } ${isQuote ? 'border-l-4 border-gray-300 pl-4 italic' : ''}`}
      dangerouslySetInnerHTML={{ __html: blockToHtml(block) }}
      data-revision={revision}
    />
  );
}, (prev, next) => prev.revision === next.revision && prev.disabled === next.disabled && prev.commandEditing === next.commandEditing);

export default function LoungeRichEditor({ value, onChange, disabled = false, uploading = false, onRequestImageUpload }) {
  const [blocksState, setBlocksState] = useState(() => normalizeLoungeEditorBlocks(value));
  const blocksRef = useRef(blocksState);
  const editorRef = useRef(null);
  const savedRangeRef = useRef(null);
  const [activeId, setActiveId] = useState(() => blocksState.find((x) => x.type === 'text')?.id || '');
  const [hasSelection, setHasSelection] = useState(false);
  const [toolbar, setToolbar] = useState({ bold: false, italic: false, underline: false, fontSize: 16, color: DEFAULT_TEXT_COLOR, link: false });
  const [linkEditorOpen, setLinkEditorOpen] = useState(false);
  const [linkDraft, setLinkDraft] = useState('https://');
  const [commandMode, setCommandMode] = useState(false);
  const [commandDraft, setCommandDraft] = useState('');

  const fingerprint = (blocks) => JSON.stringify(blocks);

  useEffect(() => {
    const incoming = normalizeLoungeEditorBlocks(value);
    if (fingerprint(incoming) === fingerprint(blocksRef.current)) return;
    blocksRef.current = incoming;
    setBlocksState(incoming);
    if (!incoming.some((block) => block.type === 'text' && block.id === activeId)) {
      setActiveId(incoming.find((x) => x.type === 'text')?.id || '');
    }
  }, [value, activeId]);

  const emit = (next, render = true) => {
    const normalized = normalizeLoungeEditorBlocks(next);
    blocksRef.current = normalized;
    if (render) setBlocksState(normalized);
    onChange?.(normalized);
  };

  const activeIndex = blocksRef.current.findIndex((block) => block.id === activeId);
  const activeTextBlock = activeIndex >= 0 && blocksRef.current[activeIndex]?.type === 'text' ? blocksRef.current[activeIndex] : null;
  const activeStyle = normalizeStyle(activeTextBlock?.style);

  useEffect(() => {
    if (commandMode) {
      const current = blocksRef.current.find((block) => block.type === 'text' && block.id === activeId);
      setCommandDraft(current ? serializeLoungeMarkup(current) : '');
    }
  }, [activeId, commandMode]);

  const toggleCommandMode = () => {
    if (!commandMode) {
      serializeActiveDom(false);
      const current = blocksRef.current.find((block) => block.type === 'text' && block.id === activeId);
      setCommandDraft(current ? serializeLoungeMarkup(current) : '');
    }
    setCommandMode((current) => !current);
  };

  const changeCommandDraft = (raw) => {
    setCommandDraft(raw);
    const idx = blocksRef.current.findIndex((block) => block.type === 'text' && block.id === activeId);
    if (idx < 0) return;
    const current = blocksRef.current[idx];
    const segments = parseLoungeMarkup(raw, current.style);
    const next = [...blocksRef.current];
    next[idx] = { ...current, segments, text: segments.map((segment) => segment.text).join(''), linkUrl: '' };
    emit(next, true);
  };

  const updateToolbarFromSelection = (range) => {
    const selection = window.getSelection?.();
    const selected = !!range && !range.collapsed;
    setHasSelection(selected);
    if (!selection || !range) return;

    let element = range.startContainer?.nodeType === Node.ELEMENT_NODE
      ? range.startContainer
      : range.startContainer?.parentElement;
    if (!element) return;
    const computed = window.getComputedStyle(element);
    const fontSize = Math.round(clamp(parseFloat(computed.fontSize), 12, 48, 16));
    const colorData = element.closest?.('[data-lounge-color]')?.getAttribute('data-lounge-color');
    const color = colorData || normalizeColor(computed.color, DEFAULT_TEXT_COLOR);
    const anchor = element.closest?.('a');

    setToolbar({
      bold: document.queryCommandState?.('bold') || ['600', '700', 'bold'].includes(String(computed.fontWeight)),
      italic: document.queryCommandState?.('italic') || computed.fontStyle === 'italic',
      underline: document.queryCommandState?.('underline') || String(computed.textDecorationLine || '').includes('underline'),
      fontSize: FONT_SIZES.reduce((best, size) => Math.abs(size - fontSize) < Math.abs(best - fontSize) ? size : best, 16),
      color: normalizeColor(color, DEFAULT_TEXT_COLOR),
      link: !!anchor,
    });
  };

  const captureSelection = (elementHint = null) => {
    const selection = window.getSelection?.();
    if (!selection || !selection.rangeCount) return false;
    const range = selection.getRangeAt(0);
    const root = editorRef.current;
    if (!root) return false;

    const container = range.commonAncestorContainer?.nodeType === Node.ELEMENT_NODE
      ? range.commonAncestorContainer
      : range.commonAncestorContainer?.parentElement;
    const editable = elementHint || container?.closest?.('[data-editor-block-id]');
    if (!editable || !root.contains(editable) || !editable.contains(range.startContainer) || !editable.contains(range.endContainer)) {
      const startInEditor = root.contains(range.startContainer);
      const endInEditor = root.contains(range.endContainer);
      if (startInEditor || endInEditor) {
        savedRangeRef.current = null;
        setHasSelection(false);
      }
      return false;
    }

    savedRangeRef.current = range.cloneRange();
    const blockId = editable.getAttribute('data-editor-block-id') || '';
    if (blockId) setActiveId(blockId);
    updateToolbarFromSelection(range);
    return true;
  };

  useEffect(() => {
    const onSelectionChange = () => captureSelection();
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  });

  const restoreSelection = () => {
    const range = savedRangeRef.current;
    const selection = window.getSelection?.();
    if (!range || !selection) return false;
    try {
      selection.removeAllRanges();
      selection.addRange(range);
      return true;
    } catch (_) {
      return false;
    }
  };

  const serializeBlockDom = (blockId, element, render = false) => {
    const idx = blocksRef.current.findIndex((block) => block.id === blockId && block.type === 'text');
    if (idx < 0 || !element) return;
    const block = blocksRef.current[idx];
    const segments = serializeEditable(element, block.style);
    const text = segments.map((segment) => segment.text).join('');
    const next = [...blocksRef.current];
    next[idx] = { ...block, text, segments, linkUrl: '' };
    emit(next, render);
  };

  const serializeActiveDom = (render = false) => {
    if (!activeId) return;
    const element = editorRef.current?.querySelector(`[data-editor-block-id="${activeId}"]`);
    if (element) serializeBlockDom(activeId, element, render);
  };

  const handleTextDomChange = (blockId, element) => {
    serializeBlockDom(blockId, element, false);
    captureSelection(element);
  };

  const patchActiveStyle = (patch) => {
    serializeActiveDom(false);
    const idx = blocksRef.current.findIndex((block) => block.id === activeId && block.type === 'text');
    if (idx < 0) return;
    const block = blocksRef.current[idx];
    const next = [...blocksRef.current];
    next[idx] = { ...block, style: { ...normalizeStyle(block.style), ...patch } };
    emit(next, true);
  };

  const execInlineCommand = (command, valueArg = null) => {
    if (disabled || !hasSelection || !restoreSelection()) return;
    document.execCommand('styleWithCSS', false, true);
    document.execCommand(command, false, valueArg);
    serializeActiveDom(false);
    captureSelection();
  };

  const applyFontSize = (size) => {
    if (disabled || !hasSelection || !restoreSelection()) return;
    const safeSize = Math.round(clamp(size, 12, 48, 16));
    document.execCommand('styleWithCSS', false, false);
    document.execCommand('fontSize', false, '7');
    const activeEl = editorRef.current?.querySelector(`[data-editor-block-id="${activeId}"]`);
    activeEl?.querySelectorAll('font[size="7"]').forEach((font) => {
      const span = document.createElement('span');
      span.style.fontSize = `${safeSize}px`;
      while (font.firstChild) span.appendChild(font.firstChild);
      font.replaceWith(span);
    });
    document.execCommand('styleWithCSS', false, true);
    serializeActiveDom(false);
    setToolbar((prev) => ({ ...prev, fontSize: safeSize }));
    captureSelection();
  };

  const applyColor = (color) => {
    if (disabled || !hasSelection || !restoreSelection()) return;
    const safeColor = normalizeColor(color, DEFAULT_TEXT_COLOR);
    document.execCommand('styleWithCSS', false, true);
    document.execCommand('foreColor', false, safeColor);
    serializeActiveDom(false);
    setToolbar((prev) => ({ ...prev, color: safeColor }));
    captureSelection();
  };

  const openLinkEditor = () => {
    if (!hasSelection || disabled) return;
    setLinkDraft('https://');
    setLinkEditorOpen(true);
  };

  const applyLink = () => {
    if (disabled || !hasSelection || !restoreSelection()) return;
    const link = normalizeLinkInput(linkDraft);
    if (!link) return;
    document.execCommand('createLink', false, link);
    const selection = window.getSelection?.();
    const node = selection?.anchorNode?.nodeType === Node.ELEMENT_NODE ? selection.anchorNode : selection?.anchorNode?.parentElement;
    const anchor = node?.closest?.('a');
    if (anchor) anchor.setAttribute('data-lounge-link', link);
    serializeActiveDom(false);
    setLinkEditorOpen(false);
    captureSelection();
  };

  const removeLink = () => {
    if (disabled || !hasSelection || !restoreSelection()) return;
    document.execCommand('unlink', false, null);
    serializeActiveDom(false);
    captureSelection();
  };

  const requestImage = () => {
    if (disabled || uploading) return;
    serializeActiveDom(false);
    const blocks = blocksRef.current;
    const idx = blocks.findIndex((block) => block.id === activeId && block.type === 'text');
    if (idx < 0) {
      onRequestImageUpload?.(blocks.length - 1);
      return;
    }

    const block = blocks[idx];
    const element = editorRef.current?.querySelector(`[data-editor-block-id="${activeId}"]`);
    const offset = element ? getCaretOffset(element) : String(block.text || '').length;
    const textLength = String(block.text || '').length;

    if (offset > 0 && offset < textLength) {
      const split = splitSegmentsAt(block.segments, offset);
      const before = { ...block, text: split.before.map((s) => s.text).join(''), segments: split.before };
      const after = { ...block, id: uid(), text: split.after.map((s) => s.text).join(''), segments: split.after };
      const next = [...blocks];
      next.splice(idx, 1, before, after);
      emit(next, true);
      setActiveId(after.id);
      onRequestImageUpload?.(idx);
      return;
    }

    onRequestImageUpload?.(offset <= 0 ? idx - 1 : idx);
  };

  const patchImageDisplay = (blockId, patch) => {
    const idx = blocksRef.current.findIndex((block) => block.id === blockId && block.type === 'image');
    if (idx < 0) return;
    const block = blocksRef.current[idx];
    const display = normalizeImageDisplay({ ...block.display, ...patch });
    const next = [...blocksRef.current];
    next[idx] = { ...block, display };
    emit(next, true);
  };

  const removeImage = (index) => {
    const next = blocksRef.current.filter((_, i) => i !== index);
    emit(next.length ? next : [createLoungeTextBlock()], true);
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
      <div className="sticky top-0 z-[2] border-b border-gray-200 bg-gray-50/95 backdrop-blur px-2 py-2">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-semibold text-gray-700">{t('라운지 본문 편집')}</span>
          <button type="button" onClick={toggleCommandMode} disabled={disabled || !activeTextBlock} className={`rounded-md border px-3 py-2 text-xs font-semibold ${commandMode ? 'border-blue-500 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700'} disabled:opacity-40`}>
            {commandMode ? '✓ 명령어 모드 ON · 일반 편집으로' : '[ / ] 명령어로 작성'}
          </button>
        </div>
        {commandMode ? (
          <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
            <p className="mb-2 text-xs font-semibold text-blue-900">선택한 텍스트 블록의 구분자 입력 · 변경 즉시 저장 데이터와 아래 미리보기에 반영됩니다.</p>
            <textarea
              value={commandDraft}
              onChange={(e) => changeCommandDraft(e.target.value)}
              disabled={disabled || !activeTextBlock}
              spellCheck={false}
              rows={8}
              aria-label="라운지 서식 명령어 입력"
              placeholder={'[size=24][b]BIG TITLE[/b][/size]\n[color=#ffcc00]Gold[/color][br]Next line [space=4] spaced'}
              className="w-full resize-y rounded-lg border border-blue-200 bg-white p-3 font-mono text-sm leading-6 text-gray-900 outline-none focus:border-blue-500"
            />
            <div className="mt-2 break-words font-mono text-[11px] text-blue-800">[b]굵게[/b] · [u]밑줄[/u] · [size=24]크기[/size] · [color=#ffcc00]색상[/color] · [br] 줄바꿈 · [space=4] 공백 4칸</div>
            <p className="mt-1 text-[11px] text-gray-500">일반 공백과 Enter도 그대로 표시됩니다. 다른 텍스트 블록을 선택하면 해당 블록의 명령어로 전환됩니다.</p>
          </div>
        ) : null}
        <div className={`flex flex-wrap items-center gap-1.5 ${commandMode ? 'pointer-events-none opacity-40' : ''}`}>
          <select
            value={activeStyle.textType}
            onChange={(e) => patchActiveStyle({ textType: e.target.value })}
            disabled={disabled || !activeTextBlock}
            className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm disabled:opacity-40"
            title={t('텍스트 종류')}
          >
            {TEXT_TYPES.map((option) => <option key={option.value} value={option.value}>{t(option.label)}</option>)}
          </select>

          <select
            value={toolbar.fontSize}
            onMouseDown={() => captureSelection()}
            onChange={(e) => applyFontSize(Number(e.target.value))}
            disabled={disabled || !hasSelection}
            className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm disabled:opacity-40"
            title={hasSelection ? t('선택한 텍스트 크기') : t('먼저 텍스트를 선택하세요')}
          >
            {FONT_SIZES.map((size) => <option key={size} value={size}>{size}px</option>)}
          </select>

          <span className="mx-0.5 h-6 w-px bg-gray-200" />

          <ToolbarButton active={toolbar.bold} disabled={disabled || !hasSelection} title={t('선택한 텍스트 굵게')} onClick={() => execInlineCommand('bold')}>B</ToolbarButton>
          <ToolbarButton active={toolbar.italic} disabled={disabled || !hasSelection} title={t('선택한 텍스트 기울임')} onClick={() => execInlineCommand('italic')}><span className="italic">I</span></ToolbarButton>
          <ToolbarButton active={toolbar.underline} disabled={disabled || !hasSelection} title={t('선택한 텍스트 밑줄')} onClick={() => execInlineCommand('underline')}><span className="underline">U</span></ToolbarButton>

          <label className={`h-9 min-w-9 rounded-md border border-gray-200 bg-white px-1.5 flex items-center justify-center ${hasSelection && !disabled ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`} title={hasSelection ? t('선택한 텍스트 색상') : t('먼저 텍스트를 선택하세요')}>
            <input
              type="color"
              value={toolbar.color}
              onMouseDown={() => captureSelection()}
              onChange={(e) => applyColor(e.target.value)}
              disabled={disabled || !hasSelection}
              className="w-6 h-6 border-0 bg-transparent p-0 cursor-pointer disabled:cursor-not-allowed"
            />
          </label>

          <span className="mx-0.5 h-6 w-px bg-gray-200" />

          {ALIGNMENTS.map((align) => (
            <ToolbarButton key={align.value} active={activeStyle.textAlign === align.value} disabled={disabled || !activeTextBlock} title={t(align.label)} onClick={() => patchActiveStyle({ textAlign: align.value })}>
              <span aria-hidden="true" className={align.value === 'left' ? 'block w-5 text-left' : align.value === 'center' ? 'block w-5 text-center' : 'block w-5 text-right'}>{align.icon}</span>
            </ToolbarButton>
          ))}

          <span className="mx-0.5 h-6 w-px bg-gray-200" />

          <ToolbarButton active={toolbar.link} disabled={disabled || !hasSelection} title={t('선택한 텍스트에 링크 넣기')} onClick={openLinkEditor}>🔗</ToolbarButton>
          <ToolbarButton disabled={disabled || !hasSelection} title={t('선택한 텍스트 링크 제거')} onClick={removeLink}>⛓×</ToolbarButton>
          <ToolbarButton disabled={disabled || uploading} title={t('현재 커서 위치에 이미지 첨부')} onClick={requestImage}>🖼</ToolbarButton>
        </div>

        {linkEditorOpen ? (
          <div className="mt-2 flex flex-col gap-2 rounded-lg border border-blue-200 bg-blue-50 p-2 sm:flex-row sm:items-center">
            <div className="shrink-0 text-xs font-semibold text-blue-800">🔗 {t('링크')}</div>
            <input
              type="url"
              value={linkDraft}
              onChange={(e) => setLinkDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
                if (e.key === 'Escape') setLinkEditorOpen(false);
              }}
              placeholder="https://example.com"
              autoFocus
              className="min-w-0 flex-1 rounded-md border border-blue-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-400"
            />
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={applyLink} disabled={disabled} className="rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-40">{t('적용')}</button>
              <button type="button" onClick={() => setLinkEditorOpen(false)} className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50">{t('취소')}</button>
            </div>
          </div>
        ) : null}

        <div className="mt-2 text-[11px] text-gray-500">
          {t('굵기·크기·색상·링크는 드래그로 선택한 글자에만 적용됩니다. Enter로 줄을 바꾸며 계속 작성할 수 있습니다.')}
        </div>
      </div>

      <div
        ref={editorRef}
        className="min-h-[460px] bg-white px-5 py-6 sm:px-8 sm:py-8 cursor-text text-gray-900"
        onClick={(e) => {
          if (disabled || e.target !== e.currentTarget) return;
          const lastText = [...blocksRef.current].reverse().find((block) => block.type === 'text');
          const el = lastText ? editorRef.current?.querySelector(`[data-editor-block-id="${lastText.id}"]`) : null;
          if (el) {
            setActiveId(lastText.id);
            el.focus();
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
            captureSelection(el);
          }
        }}
      >
        {blocksState.map((block, index) => {
          if (block.type === 'widget') {
            return <div key={block.id} className="my-4 rounded-lg border border-indigo-300 bg-indigo-50 p-4 text-sm text-indigo-900">
              <strong>위젯: {block.title || block.widgetType}</strong> · {block.widgetType === 'shop' ? `${block.offers?.length || 0}개 상품` : '예약 서버 연동 대기 (샘플 UI)'}
              <div className="text-xs text-indigo-600 mt-1">{block.widgetType === 'shop' ? '상단 위젯 편집 패널에서 SHOP 상품·금액을 변경할 수 있습니다.' : '항공편·숙소는 상품 등록 없이 샘플 화면으로 표시됩니다. 예약 서버 연결 시 실시간 정보를 사용합니다.'}</div>
            </div>;
          }
          if (block.type === 'image') {
            const display = normalizeImageDisplay(block.display);
            return (
              <div key={block.id} className="group relative my-5 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div
                  className="relative mx-auto overflow-hidden rounded-md border border-gray-200 bg-white"
                  style={{ width: `${display.widthPercent}%`, height: `${display.heightPx}px` }}
                >
                  <img
                    src={block.url}
                    alt={block.alt || ''}
                    draggable={false}
                    className="pointer-events-none absolute left-1/2 top-1/2 block h-auto max-h-none max-w-none select-none"
                    style={{
                      width: `${10000 / display.widthPercent}%`,
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                </div>
                {!disabled ? (
                  <div className="mt-3 flex flex-wrap items-center gap-3 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700">
                    <label className="flex min-w-[220px] flex-1 items-center gap-2">
                      <span className="shrink-0 font-semibold">{t('가로 표시영역')}</span>
                      <input type="range" min="20" max="100" step="1" value={display.widthPercent} onChange={(e) => patchImageDisplay(block.id, { widthPercent: Number(e.target.value) })} className="min-w-0 flex-1" />
                      <span className="w-10 text-right tabular-nums">{display.widthPercent}%</span>
                    </label>
                    <label className="flex min-w-[220px] flex-1 items-center gap-2">
                      <span className="shrink-0 font-semibold">{t('높이 표시영역')}</span>
                      <input type="range" min="100" max="720" step="10" value={display.heightPx} onChange={(e) => patchImageDisplay(block.id, { heightPx: Number(e.target.value) })} className="min-w-0 flex-1" />
                      <input type="number" min="100" max="720" step="10" value={display.heightPx} onChange={(e) => patchImageDisplay(block.id, { heightPx: Number(e.target.value) })} className="w-20 rounded border border-gray-200 px-2 py-1 text-right" />
                      <span>px</span>
                    </label>
                    <span className="text-[11px] text-gray-400">{t('가로·높이는 이미지 크기를 바꾸지 않고 보이는 영역만 조절합니다. 영역 밖 이미지는 중앙 기준으로 잘립니다.')}</span>
                    <button type="button" title={t('이미지 삭제')} onClick={() => removeImage(index)} className="ml-auto rounded border border-red-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50">{t('삭제')}</button>
                  </div>
                ) : null}
              </div>
            );
          }

          const revision = JSON.stringify({ text: block.text, style: block.style, segments: block.segments, linkUrl: block.linkUrl });
          return (
            <EditableTextBlock
              key={block.id}
              block={block}
              disabled={disabled}
              commandEditing={commandMode && activeId === block.id}
              revision={revision}
              onFocusBlock={setActiveId}
              onSelectionActivity={captureSelection}
              onDomChange={handleTextDomChange}
            />
          );
        })}
      </div>
    </div>
  );
}
